/* =========================================================================
   Shared quick-pick popover (AOV builds — CRO §02.4 / §02.5 / §04.3).
   Renders a compact option picker (dropdowns + availability cascade + close)
   into a mount element and adds the resolved variant to the cart, refreshing
   the cart drawer. Used by the cart-drawer cross-sell (af-cart-crosssell.js)
   and the PDP "Pair with" cross-sell (af-cross-add.js).

   Expects a product object shaped like the AJAX endpoints' payloads
   (/recommendations/products.json or /products/{handle}.js): prices in cents,
   `options` as [{name, values[]}], `variants[].options` positional.
   ========================================================================= */
(function () {
  if (window.AFQuickPick) return;

  function money(cents) {
    var s = (cents / 100).toFixed(2);
    return '$' + s.replace(/\.00$/, '');
  }
  function esc(s) {
    return (s || '').replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* Add a variant via the AJAX cart, swap in the fresh drawer HTML, and open
     the drawer as confirmation. cb(err) runs after the drawer refresh.
     `source` stamps a HIDDEN line-item property (_af_added_via) for
     attribution — underscore-prefixed properties are never shown to the
     shopper (drawer/cart/checkout all skip them) but flow into the order,
     so cross-sell / quick-add revenue is measurable in order exports. */
  function addToCart(variantId, cb, source, qty) {
    var item = { id: variantId, quantity: qty || 1 };
    if (source) item.properties = { _af_added_via: source };
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item], sections: ['cart-drawer'], sections_url: window.location.pathname })
    }).then(function (r) { return r.json(); }).then(function (res) {
      var html = res.sections && res.sections['cart-drawer'];
      var drawer = document.querySelector('cart-drawer');
      var cur = document.getElementById('CartDrawer');
      if (html && cur) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
        if (fresh) cur.innerHTML = fresh.innerHTML;
      }
      if (drawer) {
        drawer.classList.remove('is-empty');
        if (!drawer.classList.contains('active') && drawer.open) drawer.open();
      }
      if (cb) cb(null);
    }).catch(function (err) { if (cb) cb(err); });
  }

  /* Render the picker into `pick` (a container element). opts.onAdded() fires
     after a successful add + drawer refresh. Toggling/closing siblings is the
     caller's job — this only draws and wires one popover. */
  function open(pick, p, opts) {
    opts = opts || {};
    /* opts.qtyFor(variantId) -> units per add (default 1). Lets a grid sell in
       case quantities (kids bulk LP: quick add = one full case, which is what
       triggers the 15% ladder tier). */
    var qtyFor = function (id) { return (opts.qtyFor && opts.qtyFor(id)) || 1; };
    var sel = {};
    var start = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
    // Context-aware preselect: a size/colour collection card passes the variant
    // it matched (data-preferred-variant) so the picker opens on that variant
    // (e.g. quick-adding from the 18x24 page starts at 18x24).
    if (opts.preferVariantId) {
      var pref = p.variants.filter(function (v) { return String(v.id) === String(opts.preferVariantId) && v.available; })[0];
      if (pref) start = pref;
    }
    p.options.forEach(function (opt, i) { sel[opt.name] = start.options[i]; });

    /* Nothing to choose: a single-variant product (native bundles, one-offs)
       comes back from Shopify as one option "Title" with the value
       "Default Title" — rendering the picker shows the shopper a meaningless
       dropdown (Giorgio, #sales-dtc 27 Aug). When no option has more than one
       value, skip the popover and add the variant directly; the caller's
       onAdded still fires for the "Added" flash and GA4 attribution. A
       sold-out single variant falls through to the popover, whose confirm
       already renders the "Sold out" state. */
    var choosable = (p.options || []).some(function (opt) {
      return opt.values && opt.values.length > 1;
    });
    if (!choosable && start && start.available) {
      var q0 = qtyFor(start.id);
      addToCart(start.id, function (err) {
        if (!err && typeof window.afTrack === 'function') {
          window.afTrack('upsell_add_to_cart', {
            placement: opts.source || 'upsell',
            product_title: p.title,
            variant_title: start.title,
            variant_id: String(start.id),
            sku: start.sku || '',
            price: start.price / 100,
            quantity: q0,
            value: start.price * q0 / 100,
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
            items: [{
              item_id: start.sku || String(start.id),
              item_name: p.title,
              item_variant: start.title,
              price: start.price / 100,
              quantity: q0
            }]
          });
        }
        if (opts.onAdded) opts.onAdded(err ? null : start, err || null);
      }, opts.source, q0);
      return;
    }

    pick.hidden = false;
    pick.innerHTML = '<div class="af-xsell-pick-head"><span class="af-xsell-pick-title">Choose options</span>' +
      '<button type="button" class="af-xsell-close" aria-label="Close options">&times;</button></div>' +
      p.options.map(function (opt) {
        return '<label class="af-xsell-opt">' +
          '<span class="af-xsell-opt-name">' + esc(opt.name) + '</span>' +
          '<select class="af-xsell-select" data-opt="' + esc(opt.name) + '">' +
            opt.values.map(function (val) {
              return '<option value="' + esc(val) + '"' + (sel[opt.name] === val ? ' selected' : '') + '>' + esc(val) + '</option>';
            }).join('') +
          '</select></label>';
      }).join('') + '<button type="button" class="af-xsell-confirm"></button>';
    pick.querySelector('.af-xsell-close').addEventListener('click', function () { pick.hidden = true; pick.innerHTML = ''; });

    function resolve() {
      return p.variants.filter(function (v) {
        return p.options.every(function (opt, i) { return v.options[i] === sel[opt.name]; });
      })[0];
    }
    var confirm = pick.querySelector('.af-xsell-confirm');
    function updateConfirm() {
      var v = resolve();
      if (!v) { confirm.textContent = 'Unavailable'; confirm.disabled = true; }
      else if (!v.available) { confirm.textContent = 'Sold out'; confirm.disabled = true; }
      else {
        var q = qtyFor(v.id);
        confirm.textContent = q > 1
          ? 'Add full case (' + q + ') — ' + money(v.price * q)
          : 'Add to cart — ' + money(v.price);
        confirm.disabled = false;
      }
    }
    function optIndex(name) { for (var i = 0; i < p.options.length; i++) { if (p.options[i].name === name) return i; } return -1; }
    // Cascade: after an option changes, snap the OTHER options to the nearest
    // AVAILABLE variant that keeps the changed value — so a pick never lands on
    // a non-existent combo. Prefers variants matching the most other selections.
    function cascade(changedName) {
      var idx = optIndex(changedName), want = sel[changedName];
      var cands = p.variants.filter(function (v) { return v.available && v.options[idx] === want; });
      if (!cands.length) cands = p.variants.filter(function (v) { return v.options[idx] === want; });
      if (!cands.length) return;
      var best = cands[0], bestScore = -1;
      cands.forEach(function (v) {
        var score = 0;
        p.options.forEach(function (opt, i) { if (i !== idx && v.options[i] === sel[opt.name]) score++; });
        if (score > bestScore) { bestScore = score; best = v; }
      });
      p.options.forEach(function (opt, i) { sel[opt.name] = best.options[i]; });
      pick.querySelectorAll('.af-xsell-select').forEach(function (s) { s.value = sel[s.getAttribute('data-opt')]; });
    }
    pick.querySelectorAll('.af-xsell-select').forEach(function (s) {
      s.addEventListener('change', function () {
        sel[s.getAttribute('data-opt')] = s.value;
        cascade(s.getAttribute('data-opt'));
        updateConfirm();
      });
    });
    updateConfirm();
    confirm.addEventListener('click', function () {
      var v = resolve();
      if (!v || !v.available) return;
      var q1 = qtyFor(v.id);
      confirm.disabled = true; confirm.textContent = 'Adding…';
      addToCart(v.id, function (err) {
        /* GA4: custom event name on purpose — Shopify's Web Pixel already fires
           the standard `add_to_cart`, so re-using it here would double-count.
           Only fires on a CONFIRMED add (no error), never on picker abandons. */
        if (!err && typeof window.afTrack === 'function') {
          window.afTrack('upsell_add_to_cart', {
            placement: opts.source || 'upsell',
            product_title: p.title,
            variant_title: v.title,
            variant_id: String(v.id),
            sku: v.sku || '',
            price: v.price / 100,
            quantity: q1,
            value: v.price * q1 / 100,
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
            items: [{
              item_id: v.sku || String(v.id),
              item_name: p.title,
              item_variant: v.title,
              price: v.price / 100,
              quantity: q1
            }]
          });
        }
        /* Pass the variant + error through so callers can attribute the add.
           Existing callers take no arguments, so this stays compatible. */
        if (opts.onAdded) opts.onAdded(err ? null : v, err || null);
      }, opts.source, q1);
    });
  }

  window.AFQuickPick = { money: money, esc: esc, open: open, addToCart: addToCart };
})();
